import { EventLog } from '../event-log';
import { ErrorNotifier } from '../error-notifier';
import { STANDARD_EVENT_NAME } from '../constants';
import { formatISODate } from '../utility/date';
import { TriggeredEvent, TriggeredEventPropertyValue } from '../triggered-event.types';
import { CustomEventName } from '../ungrouped-tracking-rule.types';
import {
  EventEvaluationType,
  EvaluationPeriodSecond,
  EventEvaluationComparator,
  EventPropertyAggregationType,
  TriggerTypes,
} from './message-trigger.types';

export interface MessageTrigger {
  condition: {
    eventName: CustomEventName;
  };
  isConditionSatisfied(event: TriggeredEvent): Promise<boolean>;
}

const propertyToString = (value: TriggeredEventPropertyValue) => {
  const typeOfValue = typeof value;
  if (typeOfValue === 'string') return value as string;
  if (typeOfValue === 'number') return String(value);
  if (value === null) return 'null';
  return value as string;
};

const propertyToNumber = (value: TriggeredEventPropertyValue) => {
  return parseFloat(value as string);
};

const regexpObject = (pattern: string, ignoreCase: boolean): RegExp | null => {
  let regexp;
  try {
    if (ignoreCase) {
      regexp = new RegExp(pattern, 'i');
    } else {
      regexp = new RegExp(pattern);
    }
  } catch (e) {
    if (e instanceof SyntaxError) {
      const error = new Error(`Given RegExp pattern "${pattern}" is invalid`);
      ErrorNotifier.notify({
        error,
        extraPayload: { originalError: e },
        samplingRatio: 100,
      });
      console.warn(error);
      regexp = null;
    } else {
      throw e;
    }
  }
  return regexp;
};

class EventTrigger implements MessageTrigger {
  condition: {
    eventName: CustomEventName;
  };

  constructor(condition: { event_name: CustomEventName }) {
    this.condition = {
      eventName: condition.event_name,
    };
  }

  public isConditionSatisfied(event: TriggeredEvent): Promise<boolean> {
    return Promise.resolve(event.name === this.condition.eventName);
  }
}

class EventPropertyTrigger implements MessageTrigger {
  condition: {
    eventName: CustomEventName;
    propertyName: string;
    propertyValue: string | number;
    evaluationComparator: EventEvaluationComparator;
  };

  constructor(condition: {
    event_name: CustomEventName;
    property_name: string;
    property_value: string | number;
    evaluation_comparator: EventEvaluationComparator;
  }) {
    this.condition = {
      eventName: condition.event_name,
      propertyName: condition.property_name,
      propertyValue: condition.property_value,
      evaluationComparator: condition.evaluation_comparator,
    };
  }

  // 各Comparatorがどのような比較を行うかについての表 ↓
  // https://docs.google.com/spreadsheets/d/1ANN9FsZCyWgDELDPgubB0wbBI_C9JWf7SRuGenEMkOI/edit#gid=1861568314
  public isConditionSatisfied(event: TriggeredEvent): Promise<boolean> {
    if (event.name !== this.condition.eventName) {
      return Promise.resolve(false);
    }
    const propValue = event.properties[this.condition.propertyName];

    switch (this.condition.evaluationComparator) {
      case 'equals': {
        // null == 'null'の判定はしない
        if (propValue == null) return Promise.resolve(false);
        // Dateを入れてmillisecond単位で完全一致を狙うユースケースはありえないため一律falseとする
        // https://github.com/reproio/web-sdk/pull/650#discussion_r344606354
        if (propValue instanceof Date) return Promise.resolve(false);

        // equalsの場合には数値が比較対象として渡されている場合にも一律で文字列としての比較を行う
        if (Number.isNaN(propValue)) return Promise.resolve(false);
        const cur = propertyToString(propValue);
        const cond = String(this.condition.propertyValue);
        return Promise.resolve(cur === cond);
      }
      case 'does_not_equal': {
        // undefinedも扱う
        const cur = propertyToString(propValue);
        const cond = String(this.condition.propertyValue);
        return Promise.resolve(cur !== cond);
      }
      case 'more_than_or_equal': {
        // null => 0とはしない
        if (propValue == null) return Promise.resolve(false);
        if (propValue instanceof Date) return Promise.resolve(false);
        if (Number.isNaN(propValue)) return Promise.resolve(false);
        const cur = propertyToNumber(event.properties[this.condition.propertyName]);
        const cond = parseFloat(this.condition.propertyValue as string);
        return Promise.resolve(cur >= cond);
      }
      case 'less_than_or_equal': {
        // null => 0とはしない
        if (propValue == null) return Promise.resolve(false);
        if (propValue instanceof Date) return Promise.resolve(false);
        if (Number.isNaN(propValue)) return Promise.resolve(false);
        const cur = propertyToNumber(propValue);
        const cond = parseFloat(this.condition.propertyValue as string);
        return Promise.resolve(cur <= cond);
      }
      case 'contains': {
        if (propValue == null) return Promise.resolve(false);
        if (propValue instanceof Date) return Promise.resolve(false);
        if (Number.isNaN(propValue)) return Promise.resolve(false);
        const cur = propertyToString(propValue);
        const cond = String(this.condition.propertyValue);
        return Promise.resolve(cur.indexOf(cond) !== -1);
      }
      case 'does_not_contain': {
        if (propValue == null) return Promise.resolve(false);
        if (propValue instanceof Date) return Promise.resolve(false);
        if (Number.isNaN(propValue)) return Promise.resolve(false);
        const cur = propertyToString(propValue);
        const cond = String(this.condition.propertyValue);
        return Promise.resolve(cur.indexOf(cond) === -1);
      }
      case 'match_regexp': {
        // return false if passed falsy value or Data object
        if (propValue == null) return Promise.resolve(false);
        if (propValue instanceof Date) return Promise.resolve(false);
        if (Number.isNaN(propValue)) return Promise.resolve(false);

        const cur = propertyToString(propValue);
        const cond = regexpObject(propertyToString(this.condition.propertyValue), false);
        if (cond == null) return Promise.resolve(false);
        return Promise.resolve(cur.match(cond) !== null);
      }
      case 'match_regexp_ignore_case': {
        // return false if passed falsy value or Data object
        if (propValue == null) return Promise.resolve(false);
        if (propValue instanceof Date) return Promise.resolve(false);
        if (Number.isNaN(propValue)) return Promise.resolve(false);

        const cur = propertyToString(propValue);
        const cond = regexpObject(propertyToString(this.condition.propertyValue), true);
        if (cond == null) return Promise.resolve(false);
        return Promise.resolve(cur.match(cond) !== null);
      }
      case 'does_not_match_regexp': {
        // return true if passed falsy value or Data object
        if (propValue == null) return Promise.resolve(false);
        if (propValue instanceof Date) return Promise.resolve(false);
        if (Number.isNaN(propValue)) return Promise.resolve(false);

        const cur = propertyToString(propValue);
        const cond = regexpObject(propertyToString(this.condition.propertyValue), false);
        if (cond == null) return Promise.resolve(false);
        return Promise.resolve(cur.match(cond) === null);
      }
      case 'does_not_match_regexp_ignore_case': {
        // return true if passed falsy value or Data object
        if (propValue == null) return Promise.resolve(false);
        if (propValue instanceof Date) return Promise.resolve(false);
        if (Number.isNaN(propValue)) return Promise.resolve(false);

        const cur = propertyToString(propValue);
        const cond = regexpObject(propertyToString(this.condition.propertyValue), true);
        if (cond == null) return Promise.resolve(false);
        return Promise.resolve(cur.match(cond) === null);
      }
      default: {
        return Promise.reject(new Error(`invalid comparator ${this.condition.evaluationComparator}`));
      }
    }
  }
}

class EventOccurrenceTrigger implements MessageTrigger {
  condition: {
    eventName: CustomEventName;
    evaluationType: EventEvaluationType;
    evaluationPeriodSecond: EvaluationPeriodSecond;
    evaluationComparator: EventEvaluationComparator;
    occurrence: number;
  };

  private eventLog: EventLog;

  constructor(
    condition: {
      event_name: CustomEventName;
      evaluation_type: EventEvaluationType;
      evaluation_period_second: EvaluationPeriodSecond;
      evaluation_comparator: EventEvaluationComparator;
      occurrence: number;
    },
    eventLog: EventLog
  ) {
    this.condition = {
      eventName: condition.event_name,
      evaluationType: condition.evaluation_type,
      evaluationPeriodSecond: condition.evaluation_period_second,
      evaluationComparator: condition.evaluation_comparator,
      occurrence: condition.occurrence,
    };
    this.eventLog = eventLog;
  }

  public isConditionSatisfied(event: TriggeredEvent): Promise<boolean> {
    if (this.condition.eventName !== event.name) {
      return Promise.resolve(false);
    }

    return this.eventLog
      .buildQuery(this.condition.eventName)
      .setEvaluationType(this.condition.evaluationType)
      .setEvaluationPeriodSecond(this.condition.evaluationPeriodSecond)
      .execute()
      .then((events) => {
        const actualOccurrences = events.length;
        switch (this.condition.evaluationComparator) {
          case 'more_than_or_equal':
            return actualOccurrences >= this.condition.occurrence;
          case 'equals':
            return actualOccurrences === this.condition.occurrence;
          case 'less_than_or_equal':
            return actualOccurrences <= this.condition.occurrence;
          default: {
            const error = new Error(`evaluationComparator ${this.condition.evaluationComparator} is not valid`);
            ErrorNotifier.notify({
              error,
              extraPayload: {
                event,
                actualOccurrences,
                condition: this.condition,
              },
            });
            console.warn(error);
            return Promise.reject(error);
          }
        }
      });
  }
}

class AggregatedEventPropertyTrigger implements MessageTrigger {
  condition: {
    eventName: CustomEventName;
    evaluationType: EventEvaluationType;
    evaluationPeriodSecond: EvaluationPeriodSecond;
    evaluationComparator: EventEvaluationComparator;
    propertyName: string;
    aggregateType: EventPropertyAggregationType;
    aggregatedValueThreshold: number;
  };

  private eventLog: EventLog;

  constructor(
    condition: {
      event_name: CustomEventName;
      evaluation_type: EventEvaluationType;
      evaluation_period_second: EvaluationPeriodSecond;
      evaluation_comparator: EventEvaluationComparator;
      property_name: string;
      aggregate_type: EventPropertyAggregationType;
      aggregated_value_threshold: number;
    },
    eventLog: EventLog
  ) {
    this.condition = {
      eventName: condition.event_name,
      evaluationType: condition.evaluation_type,
      evaluationPeriodSecond: condition.evaluation_period_second,
      evaluationComparator: condition.evaluation_comparator,
      propertyName: condition.property_name,
      aggregateType: condition.aggregate_type,
      aggregatedValueThreshold: condition.aggregated_value_threshold,
    };
    this.eventLog = eventLog;
  }

  public isConditionSatisfied(event: TriggeredEvent): Promise<boolean> {
    if (this.condition.eventName !== event.name) {
      return Promise.resolve(false);
    }

    return this.eventLog
      .buildQuery(this.condition.eventName)
      .setEvaluationType(this.condition.evaluationType)
      .setEvaluationPeriodSecond(this.condition.evaluationPeriodSecond)
      .execute()
      .then((events) => {
        let aggregatedValue: number;
        switch (this.condition.aggregateType) {
          case 'sum': {
            aggregatedValue = events.reduce((r: number, e: TriggeredEvent) => {
              const i = parseFloat(e.properties[this.condition.propertyName] as string);
              if (!isNaN(i)) {
                return r + i;
              }
              return r;
            }, 0);
            break;
          }
          default: {
            const error = new Error(`unknown aggregation type:${this.condition.aggregateType}`);
            ErrorNotifier.notify({
              error,
              extraPayload: {
                event,
                condition: this.condition,
              },
            });
            console.warn(error);
            return Promise.reject(error);
          }
        }
        switch (this.condition.evaluationComparator) {
          case 'more_than_or_equal':
            return aggregatedValue >= this.condition.aggregatedValueThreshold;
          case 'equals':
            return aggregatedValue === this.condition.aggregatedValueThreshold;
          case 'less_than_or_equal':
            return aggregatedValue <= this.condition.aggregatedValueThreshold;
          case 'does_not_equal':
            return aggregatedValue !== this.condition.aggregatedValueThreshold;
          default: {
            const error = new Error(`evaluationComparator ${this.condition.evaluationComparator} is not valid`);
            ErrorNotifier.notify({
              error,
              extraPayload: {
                event,
                aggregatedValue,
                condition: this.condition,
              },
            });
            console.warn(error);
            return Promise.reject(error);
          }
        }
      });
  }
}

class SetUserProfileTrigger implements MessageTrigger {
  condition: {
    eventName: CustomEventName;
    userProfileName: string;
    userProfileValue: string;
  };

  public constructor(condition: { user_profile_name: string; user_profile_value: string }) {
    this.condition = {
      eventName: STANDARD_EVENT_NAME('set_user_profile'),
      userProfileName: condition.user_profile_name,
      userProfileValue: condition.user_profile_value,
    };
  }

  public isConditionSatisfied(event: TriggeredEvent): Promise<boolean> {
    if (this.condition.eventName !== event.name || this.condition.userProfileName !== event.properties.key) {
      return Promise.resolve(false);
    }

    if (event.properties.type === 'datetime') {
      const dateValue = new Date(this.condition.userProfileValue);
      if (isNaN(dateValue.getTime())) {
        return Promise.resolve(false);
      }
      return Promise.resolve(formatISODate(dateValue) === event.properties.value);
    }

    // Using type-casting == (rather than strict ===) because the type of this.userProfileValue
    // cannot be set by the user (it is always a string value)
    return Promise.resolve(this.condition.userProfileValue == event.properties.value); // eslint-disable-line
  }
}

export class MessageTriggerBuilder {
  public static build(type: TriggerTypes, condition: FIXME, eventLog: EventLog): MessageTrigger {
    switch (type) {
      case '': // initialized during preview mode only
      case 'event':
        return new EventTrigger(condition);

      case 'event_occurrence':
        return new EventOccurrenceTrigger(condition, eventLog);

      case 'event_property':
        return new EventPropertyTrigger(condition);

      case 'event_property_aggregation':
        return new AggregatedEventPropertyTrigger(condition, eventLog);

      case 'set_user_profile':
        return new SetUserProfileTrigger(condition);

      default: {
        const error = new Error(`type ${type} is not valid`);
        ErrorNotifier.notify({
          error,
          extraPayload: {
            type,
            condition,
          },
        });
        throw error;
      }
    }
  }
}
